import React, { useState, useEffect } from 'react'
import { getFromApi, postToApi, putToApi } from 'actions/index'
import OperationForm from './form'
import OperationTable from './table'
import SecurityAdministrationLayout from '../../commons/SecurityAdministrationLayout'
import { VALUES } from 'utils/constants'
import { buildFiltersDescriptors } from '../../commons/SearchFilters'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import { useStateValue } from 'context/store'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import { openSnackBarAction } from 'context/reducers/openSnackbarReducer'

const INITIAL_OPERATION_FORM_STATE = {
   name: '',
   description: '',
   types: [],
   application: null,
   module: null,
   functionality: null,
   status: ''
}

const TITLE = {
   [VALUES.FORM_BEHAVIOR.REGISTER]: 'Registrar Operación',
   [VALUES.FORM_BEHAVIOR.SEARCH]: 'Consultar Operación',
   [VALUES.FORM_BEHAVIOR.EDIT]: 'Editar Operación'
}

const { STATUS_OPTIONS } = VALUES

function getOperationTypeValues(typesIds) {
   return typesIds.map(typeId => VALUES.OPERATION_TYPES.find(oper_type => oper_type.value === typeId))
}

const Operation = ({ isSearch }) => {
   const [isShowingPopover, setPopoverProp] = useState(true)
   const [allActiveApplications, setAllActiveApplications] = useState([])
   const [modulesByAppId, setModulesByAppId] = useState([])
   const [functionalitiesByModId, setFunctionalitiesByModId] = useState([])

   const [formData, setFormData] = useState({})
   const [searchFilters, setSearchFilters] = useState({})
   const [tableData, setTableData] = useState({})
   const [paginationParams, setPaginationParams] = useState({})
   const [formBehavior, setFormBehavior] = useState(
      isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER
   )
   const [options, setOptions] = useState([...STATUS_OPTIONS])

   const [_, dispatch] = useStateValue()

   // ---------------- Clean Form -----------------
   function cleanForm() {
      setFormData({
         ...INITIAL_OPERATION_FORM_STATE
      })
      setFormBehavior(isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER)
   }

   function clean() {
      setSearchFilters({})
      setTableData(VALUES.TABLE_DATA_INTIAL_STATE)
      setPopoverProp(true)
      setPaginationParams(VALUES.PAGING_ORDER_INITIAL_STATE)
      cleanForm()
   }

   useEffect(() => {
      clean()
   }, [isSearch]) // eslint-disable-line react-hooks/exhaustive-deps

   // ------------- Fetch all Initial Active Applications  -----------------
   async function _getAllActiveApplications() {
      const url = `${process.env.REACT_APP_APPLICATIONS_API}/applications/active`
      const activeApplications = await getFromApi(url)
      setAllActiveApplications(activeApplications)
   }

   const getAllActiveApplications = useApiRequest(
      _getAllActiveApplications,
      'Operaciones: Consultar Aplicaciones Activas'
   )

   useEffect(() => {
      getAllActiveApplications()
   }, [])

   // ------------- Fetch all Modules and Funcs by ids  -----------------
   async function _getModulesByApplicationId(applicationId) {
      const payload = {
         applicationId,
         status: 'A',
         page: 0,
         size: 500,
         order: 'asc',
         columnOrder: 'name'
      }
      const url = `${process.env.REACT_APP_MODULES_API}/modules/search`
      const { data: searchedModules } = await getFromApi(url, payload)
      setModulesByAppId(searchedModules)
   }

   const getModulesByApplicationId = useApiRequest(
      _getModulesByApplicationId,
      'Operaciones: Consultar Módulos por Id de Aplicación'
   )

   async function _getFunctionalitiesByModuleId(moduleId) {
      const payload = {
         moduleId,
         status: 'A',
         page: 0,
         size: 500,
         order: 'asc',
         columnOrder: 'name'
      }
      const url = `${process.env.REACT_APP_FUNCTIONALITIES_API}/functionalities/search`
      const { data: searchedFunctionalities } = await getFromApi(url, payload)
      setFunctionalitiesByModId(searchedFunctionalities)
      const functionalityFilters = searchedFunctionalities.map(mod => ({
         label: mod.name,
         value: mod.id
      }))
      setOptions(prev => [...prev, ...functionalityFilters])
   }

   const getFunctionalitiesByModuleId = useApiRequest(
      _getFunctionalitiesByModuleId,
      'Operaciones: Consultar Funcionalidades por Id de Módulo'
   )

   // ------------ Search Operations -----------------
   function getFiltersDescriptors(filters) {
      let { types, functionality, ...otherFilters } = filters
      otherFilters = omit(otherFilters, ['application', 'module'])
      const descriptors =
         buildFiltersDescriptors(options)({
            ...otherFilters,
            functionality: functionality?.id
         }) || []
      if (!isEmpty(types)) {
         return descriptors.concat(types)
      }
      return descriptors
   }

   async function removeSearchFilter(filter) {
      const currentFilters = { ...searchFilters }
      const removeType = (types, type) => types.filter(t => t.value !== type.value)
      if (!filter.filterName) {
         await searchOperations({
            ...currentFilters,
            types: removeType(currentFilters.types, filter)
         })
      } else {
         await searchOperations(omit(currentFilters, [filter.filterName]), VALUES.PAGING_ORDER_INITIAL_STATE)
      }
   }

   async function _searchOperations(externalFilters, externalPagingAndOrder = {}) {
      const filters = externalFilters ? externalFilters : searchFilters
      const pagingFilters = { ...paginationParams, ...externalPagingAndOrder }
      const payload = new URLSearchParams()
      Object.keys(filters).forEach(filterName => {
         const filter = filters[filterName]
         if (filterName === 'types') {
            filters.types.forEach(type => {
               payload.append('types', type.value)
            })
         } else if (filterName === 'functionality') {
            if (filter && filter.id) {
               payload.append('functionalityId', filter.id)
            }
         } else if (filter && filterName !== 'application' && filterName !== 'module') {
            payload.append(filterName, filter)
         }
      })
      payload.append('page', pagingFilters.page)
      payload.append('size', pagingFilters.size)
      payload.append('order', pagingFilters.order)
      payload.append('columnOrder', pagingFilters.columnOrder)
      setSearchFilters(filters)
      setPaginationParams(pagingFilters)
      const url = `${process.env.REACT_APP_OPERATIONS_API}/operations/search`
      const searchedOperations = await getFromApi(url, payload)
      setTableData(searchedOperations)
      setFormData(INITIAL_OPERATION_FORM_STATE)
   }

   const searchOperations = useApiRequest(_searchOperations, 'Operaciones: Consultar')

   // ------------ Register Operations -----------------
   async function _registerOperation() {
      const payload = {
         name: formData.name,
         description: formData.description,
         types: formData.types.map(type => type.value),
         status: formData.status,
         functionalityId: formData.functionality.id
      }
      const url = `${process.env.REACT_APP_OPERATIONS_API}/operations`
      const registeredOperation = await postToApi(url, payload)
      delete registeredOperation.link // TODO: esta linea se quita cuando no llegue la prop desde backend
      setTableData({
         ...VALUES.TABLE_DATA_INTIAL_STATE,
         data: [registeredOperation]
      })
      setFormData(INITIAL_OPERATION_FORM_STATE)
      dispatch(openSnackBarAction(`Se guardó correctamente la Operación ${registeredOperation.name}`, 'info'))
   }

   const registerOperation = useApiRequest(_registerOperation, 'Operaciones: Registrar')

   // -------------- Edit Operations ----------------
   const _editOperations = async () => {
      const payload = {
         name: formData.name,
         description: formData.description,
         types: formData.types.map(type => type.value),
         status: formData.status,
         functionalityId: formData.functionality.id
      }
      const url = `${process.env.REACT_APP_OPERATIONS_API}/operations/${formData.id}`
      const editedOperation = await putToApi(url, payload)
      setFormBehavior(isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER)
      setFormData(INITIAL_OPERATION_FORM_STATE)
      setTableData({
         ...VALUES.TABLE_DATA_INTIAL_STATE,
         data: [editedOperation]
      })
      dispatch(openSnackBarAction(`Se actualizó correctamente la Operación ${editedOperation.name}`, 'info'))
   }

   const editOperations = useApiRequest(_editOperations, 'Operaciones: Editar')

   // -------------- Form Actions ---------------
   function actionSubmit(type) {
      setPopoverProp(false)
      if (type === VALUES.FORM_BEHAVIOR.REGISTER) {
         registerOperation()
      }
      if (type === VALUES.FORM_BEHAVIOR.EDIT) {
         editOperations()
      }
      if (type === VALUES.FORM_BEHAVIOR.SEARCH) {
         searchOperations(formData, VALUES.PAGING_ORDER_INITIAL_STATE)
      }
   }

   function actionEdit(rowData) {
      setFormBehavior(VALUES.FORM_BEHAVIOR.EDIT)
      setPopoverProp(true)
      setFormData({
         id: rowData.id,
         name: rowData.name,
         description: rowData.description,
         types: getOperationTypeValues(rowData.types),
         status: rowData.status,
         functionality: rowData.functionality,
         application: rowData.functionality.module,
         module: rowData.functionality.module.application
      })
   }

   return (
      <SecurityAdministrationLayout
         title={TITLE[formBehavior]}
         form={
            <OperationForm
               formData={formData}
               setFormData={setFormData}
               cleanCallback={cleanForm}
               actionSubmit={actionSubmit}
               formBehavior={formBehavior}
               applications={allActiveApplications}
               modules={modulesByAppId}
               setModules={setModulesByAppId}
               functionalities={functionalitiesByModId}
               setFunctionalities={setFunctionalitiesByModId}
               getFunctionalitiesByModuleId={getFunctionalitiesByModuleId}
               getAllActiveApplications={getAllActiveApplications}
               getModulesByApplicationId={getModulesByApplicationId}
            />
         }
         table={
            <OperationTable
               operations={tableData}
               isSearch={!!isSearch}
               searchOperations={searchOperations}
               cleanCallback={clean}
               order={paginationParams.order}
               actionEdit={actionEdit}
            />
         }
         popoverProp={isShowingPopover}
         setPopoverProp={setPopoverProp}
         searchFilters={getFiltersDescriptors(searchFilters)}
         onRemoveFilter={removeSearchFilter}
         search={searchOperations}
      />
   )
}

export default Operation
